import { useEffect, useMemo, useRef, useState } from "react";
import Globe from "react-globe.gl";

const SEVERITY_COLOR = {
  low:      "#00F0FF",
  medium:   "#FFC542",
  high:     "#FF6B35",
  critical: "#FF2E63",
};

const ARC_LIFETIME_MS = 6000;    // how long an arc stays visible on the globe
const RING_LIFETIME_MS = 4000;   // pulse ring lifetime

/**
 * ThreatGlobe — cinematic 3D globe for SOC wall displays.
 *
 * Props:
 *  - events: rolling buffer of the same event objects the 2D map consumes
 *  - onSelect: called with an event when a marker is clicked
 *  - autoRotate: default true (wall mode)
 *  - wallMode: bool — expands the globe to fill viewport
 *  - onCritEvent: hook fired for the first time a CRIT event enters view
 */
export default function ThreatGlobe({
  events = [],
  onSelect,
  autoRotate = true,
  wallMode = false,
  onCritEvent,
}) {
  const globeRef = useRef(null);
  const containerRef = useRef(null);
  const [dims, setDims] = useState({ w: 800, h: 600 });
  const seenIdsRef = useRef(new Set());
  const [ephemeralArcs, setEphemeralArcs] = useState([]);
  const [ephemeralRings, setEphemeralRings] = useState([]);

  // Watch container for size (works even when wall mode toggles)
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      setDims({ w: Math.max(100, r.width), h: Math.max(100, r.height) });
    });
    ro.observe(el);
    // Initial size
    const r = el.getBoundingClientRect();
    setDims({ w: Math.max(100, r.width), h: Math.max(100, r.height) });
    return () => ro.disconnect();
  }, [wallMode]);

  // Configure controls (auto-rotate, min/max zoom) once globe is ready.
  useEffect(() => {
    if (!globeRef.current) return;
    try {
      const controls = globeRef.current.controls?.();
      if (controls) {
        controls.autoRotate = autoRotate;
        controls.autoRotateSpeed = 0.35;
        controls.enableDamping = true;
        controls.dampingFactor = 0.08;
        controls.minDistance = 180;
        controls.maxDistance = 500;
      }
      globeRef.current.pointOfView?.({ altitude: 2.4 }, 0);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("Globe controls setup skipped:", e?.message);
    }
  }, [autoRotate]);

  // Detect new events and spawn ephemeral arcs + rings; camera-swoop on CRIT.
  useEffect(() => {
    if (!events.length) return;
    const fresh = [];
    for (const ev of events) {
      if (!seenIdsRef.current.has(ev.id)) {
        seenIdsRef.current.add(ev.id);
        fresh.push(ev);
      }
    }
    if (!fresh.length) return;

    const now = Date.now();
    const newArcs = fresh.map((ev) => ({
      id: ev.id,
      startLat: ev.source.lat,
      startLng: ev.source.lon,
      endLat: ev.target.lat,
      endLng: ev.target.lon,
      color: [SEVERITY_COLOR[ev.severity], "#00F0FF"],
      severity: ev.severity,
      _created: now,
      _lifetime: ARC_LIFETIME_MS,
      _ev: ev,
    }));
    const newRings = fresh.map((ev) => ({
      id: `${ev.id}-src`,
      lat: ev.source.lat,
      lng: ev.source.lon,
      severity: ev.severity,
      _color: SEVERITY_COLOR[ev.severity],
      _created: now,
      _lifetime: RING_LIFETIME_MS,
    }));

    setEphemeralArcs((prev) => [...prev, ...newArcs]);
    setEphemeralRings((prev) => [...prev, ...newRings]);

    // Camera swoop + emit onCritEvent for CRIT hits.
    const crit = fresh.find((e) => e.severity === "critical");
    if (crit) {
      onCritEvent?.(crit);
      try {
        if (globeRef.current) {
          const controls = globeRef.current.controls?.();
          const prevAutoRotate = controls?.autoRotate ?? true;
          if (controls) controls.autoRotate = false;
          globeRef.current.pointOfView?.(
            { lat: crit.source.lat, lng: crit.source.lon, altitude: 1.6 },
            1400,
          );
          setTimeout(() => {
            try { if (controls) controls.autoRotate = prevAutoRotate; }
            catch { /* controls torn down during view switch */ }
          }, 2200);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Globe swoop skipped:", e?.message);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  // GC ephemeral arcs & rings periodically.
  useEffect(() => {
    const t = setInterval(() => {
      const now = Date.now();
      setEphemeralArcs((prev) => prev.filter((a) => now - a._created < a._lifetime));
      setEphemeralRings((prev) => prev.filter((r) => now - r._created < r._lifetime));
    }, 800);
    return () => clearInterval(t);
  }, []);

  // Aggregated hotspot points for persistent "hot" origins.
  const hotspotPoints = useMemo(() => {
    const agg = new Map();
    for (const e of events) {
      const key = `${e.source.city}|${e.source.country}`;
      const cur = agg.get(key) || {
        lat: e.source.lat, lng: e.source.lon,
        city: e.source.city, country: e.source.country,
        count: 0, maxSev: 0,
      };
      cur.count += 1;
      cur.maxSev = Math.max(cur.maxSev, e.severity_weight || 1);
      agg.set(key, cur);
    }
    return [...agg.values()];
  }, [events]);

  const targetPoints = useMemo(() => {
    const agg = new Map();
    for (const e of events.slice(-24)) {
      const key = `${e.target.city}|${e.target.country}`;
      const cur = agg.get(key) || {
        lat: e.target.lat, lng: e.target.lon,
        city: e.target.city, country: e.target.country,
        count: 0,
      };
      cur.count += 1;
      agg.set(key, cur);
    }
    return [...agg.values()];
  }, [events]);

  const sevColorFor = (w) =>
    w >= 4 ? SEVERITY_COLOR.critical :
    w >= 3 ? SEVERITY_COLOR.high :
    w >= 2 ? SEVERITY_COLOR.medium : SEVERITY_COLOR.low;

  // No custom globeMaterial — react-globe.gl's default handles the earth-night
  // texture cleanly. Importing three directly here was creating a second three
  // instance and triggering intermittent 'instanceof' bugs on rapid view swaps.

  return (
    <div ref={containerRef} className="w-full h-full relative" data-testid="threat-globe">
      <Globe
        ref={globeRef}
        width={dims.w}
        height={dims.h}
        backgroundColor="rgba(0,0,0,0)"
        globeImageUrl="//unpkg.com/three-globe/example/img/earth-night.jpg"
        bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
        showAtmosphere
        atmosphereColor="#00F0FF"
        atmosphereAltitude={0.15}
        showGraticules={false}

        // Country polygons — dark grey with cyan hairlines
        polygonsData={[]}   /* topology could be wired via countries geojson if desired */

        // Persistent hotspot points (origins) — sized by attack count
        pointsData={hotspotPoints}
        pointLat="lat"
        pointLng="lng"
        pointColor={(p) => sevColorFor(p.maxSev)}
        pointAltitude={(p) => Math.min(0.35, 0.02 + p.count * 0.012)}
        pointRadius={(p) => Math.min(0.7, 0.16 + p.count * 0.03)}
        pointsMerge={false}
        pointLabel={(p) => `
          <div style="background:#0a0a0a;border:1px solid #00F0FF44;padding:6px 10px;font-family:'IBM Plex Mono',monospace;font-size:11px;color:#fff">
            <div style="color:#00F0FF">${p.city} / ${p.country}</div>
            <div style="color:#8E9196">${p.count} events · sev ${p.maxSev}</div>
          </div>`}

        // Ripple rings on origin cities for freshly-arrived events
        ringsData={ephemeralRings}
        ringLat="lat"
        ringLng="lng"
        ringColor={(r) => (t) => {
          // t is 0..1 through animation cycle. Use rgba with alpha decay.
          const c = r._color || "#00F0FF";
          return `${c}${Math.floor((1 - t) * 200).toString(16).padStart(2, "0")}`;
        }}
        ringMaxRadius={(r) => (r.severity === "critical" ? 8 : r.severity === "high" ? 6 : 4)}
        ringPropagationSpeed={2.2}
        ringRepeatPeriod={800}

        // Arcs from source → target with severity-based color gradient
        arcsData={ephemeralArcs}
        arcStartLat="startLat"
        arcStartLng="startLng"
        arcEndLat="endLat"
        arcEndLng="endLng"
        arcColor="color"
        arcAltitudeAutoScale={0.5}
        arcStroke={0.4}
        arcDashLength={0.45}
        arcDashGap={2.5}
        arcDashInitialGap={1}
        arcDashAnimateTime={1800}
        onArcClick={(a) => a?._ev && onSelect?.(a._ev)}

        // Small target markers on freshest destinations
        labelsData={targetPoints}
        labelLat="lat"
        labelLng="lng"
        labelText={() => "◉"}
        labelColor={() => "#00F0FF"}
        labelSize={0.5}
        labelDotRadius={0.15}
        labelResolution={2}
        labelAltitude={0.005}
      />
    </div>
  );
}
